import { Block } from '@aloft/toolkit-sui';

import 'mapbox-gl/dist/mapbox-gl.css';
import { memo, useMemo, useRef, useState } from 'react';

import { StyleRadio } from '../../../../components/map/StyleRadio.js';
import { INITIAL_STYLE, MAP_PIN_STATUS_FILTERS } from '../../../../components/map/constants.js';
import { Pin, PinStatusFilter } from '../../../../components/map/types.js';
import { useMap } from '../../../../components/map/useMap.js';
import { PairedSaleIDs } from '../../types.js';
import { PairedSalesMapPropertyTooltip } from './PairedSalesMapPropertyTooltip.js';
import { PairedSalesMapProps, PinsSourceFeature } from './types.js';
import { useRedrawPolygon } from './useRedrawPolygon.js';
import { useResizeMap } from './useResizeMap.js';
import { useSetPinsSource } from './useSetPinsSource.js';

import './PairedSalesMap.scss';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const Map = ({
  mapRef,
  pairs,
  properties,
  coordinates,
  subject,
  zoom,
  center,
  selectedPairs,
  activePair,
  onPinClick,
  onTooltipClose,
  onPolygonCreateAndUpdate,
  onPolygonDrawDelete,
  onMoveEnd,
}: PairedSalesMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [mapStyle, setMapStyle] = useState(INITIAL_STYLE.url);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isPinSourceLoaded, setIsPinSourceLoaded] = useState(false);

  const property1 = activePair ? properties[activePair.p1] : undefined;
  const property2 = activePair ? properties[activePair.p2] : undefined;

  const pins = useMemo((): Pin[] => {
    if (
      !property1?.latitude ||
      !property1?.longitude ||
      !property2?.latitude ||
      !property2?.longitude
    ) {
      return [];
    }

    return [
      {
        id: property1.toolkitRecordID || '',
        lat: property1.latitude,
        lng: property1.longitude,
        label: property1.address?.AddressLineText || '',
        icon: (property1.listingStatus as PinStatusFilter) || MAP_PIN_STATUS_FILTERS.OTHER,
        status: (property1.listingStatus as PinStatusFilter) || MAP_PIN_STATUS_FILTERS.OTHER,
        active: false,
        selected: false,
      },
      {
        id: property2.toolkitRecordID || '',
        lat: property2.latitude,
        lng: property2.longitude,
        label: property2.address?.AddressLineText || '',
        icon: (property2.listingStatus as PinStatusFilter) || MAP_PIN_STATUS_FILTERS.OTHER,
        status: (property2.listingStatus as PinStatusFilter) || MAP_PIN_STATUS_FILTERS.OTHER,
        active: false,
        selected: false,
      },
    ];
  }, [property1, property2]);

  useMap<PinsSourceFeature>({
    mapContainerRef,
    mapRef,
    subject,
    center,
    zoom,
    pins,
    enablePolygonDraw: true,
    callbacks: {
      onMapLoad: () => {
        setIsMapLoaded(true);
      },
      onPinSourceLoad: () => {
        setIsPinSourceLoaded(true);
      },
      onPinClick: (feature) => {
        if (!feature) return;

        // MapBox stringifies feature props. See https://github.com/mapbox/mapbox-gl-js/issues/2434
        const pair: PairedSaleIDs = JSON.parse(feature.properties.pair as unknown as string);
        onPinClick(pair);
      },
      onMoveEnd: ({ center, zoom }) => onMoveEnd(center, zoom),
      onPolygonDrawCreate: onPolygonCreateAndUpdate,
      onPolygonDrawUpdate: onPolygonCreateAndUpdate,
      onPolygonDrawDelete: () => {
        mapRef.current?.getDraw().changeMode('draw_polygon');
        onPolygonDrawDelete();
      },
    },
  });

  useRedrawPolygon({
    mapRef,
    coordinates,
  });

  useResizeMap({ mapRef, mapContainerRef });

  useSetPinsSource({
    mapRef,
    pairs,
    selectedPairs,
    properties,
    canSetSource: isMapLoaded && isPinSourceLoaded,
  });

  return (
    <Block ref={mapContainerRef} height="100%" position="relative" zIndex="0" overflow="hidden">
      <Block
        position="absolute"
        bottom="10px"
        right="10px"
        zIndex="1"
        backgroundColor="textOffset"
        padding="8px 16px"
        borderRadius="4px"
      >
        <StyleRadio mapRef={mapRef} style={mapStyle} onStyleChange={setMapStyle} />
      </Block>
      <PairedSalesMapPropertyTooltip
        property={property1}
        onTooltipClose={onTooltipClose}
        mapRef={mapRef}
      />
      <PairedSalesMapPropertyTooltip
        property={property2}
        onTooltipClose={onTooltipClose}
        mapRef={mapRef}
      />
    </Block>
  );
};

export const PairedSalesMap = memo(Map);
